import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { fmtIST } from "@/lib/datetime";
import { Package, Plus, CheckCircle, Clock, MapPin, Buildings, Truck, User, Camera, ArrowRight, CaretUpDown, Check } from "@phosphor-icons/react";

const STATUS_META = {
    expected:        { label: "Expected",           tone: "amber",   dot: 1 },
    arrived_at_gate: { label: "At Gate",            tone: "sky",     dot: 2 },
    received:        { label: "Received",           tone: "emerald", dot: 3 },
};
const TONE = {
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
    sky:     "bg-sky-50 text-sky-700 border-sky-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    slate:   "bg-slate-50 text-slate-500 border-slate-200",
};

function StageDots({ stage }) {
    // ● ● ● — filled dots match the current stage number.
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3].map((n) => (
                <div key={n} className={`h-1.5 w-1.5 rounded-full ${n <= stage ? "bg-brand" : "bg-slate-200"}`} />
            ))}
        </div>
    );
}
function StatusPill({ status }) {
    const m = STATUS_META[status] || STATUS_META.expected;
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-semibold ${TONE[m.tone]}`}>{m.label}</span>;
}

const TABS = [
    { key: "auto",          label: "Default",       hint: "Your dept — or all if you're a superset viewer" },
    { key: "dept",          label: "For my dept",   hint: "Filter to your own department only" },
    { key: "mine",          label: "I logged",      hint: "Your Stage-1 entries" },
    { key: "awaiting-gate", label: "Awaiting gate", hint: "Logged but not yet arrived (guard focus)" },
    { key: "all",           label: "All",           hint: "Everything (superset viewers only)" },
];

export default function Deliveries() {
    const { user } = useAuth();
    const superset = ["admin", "admin_addl", "guard", "management"].includes(user?.role);
    const has = (cap) => (user?.capabilities || []).includes(cap) || superset;
    const [scope, setScope] = useState(superset ? "auto" : "dept");
    const [items, setItems] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [gates, setGates] = useState([]);
    const [logOpen, setLogOpen] = useState(false);
    const [arrivedFor, setArrivedFor] = useState(null);  // delivery obj — opens Stage-2 dialog
    const [receiveFor, setReceiveFor] = useState(null);  // delivery obj — opens Stage-3 dialog
    const [detail, setDetail] = useState(null);          // read-only timeline drawer

    const load = async () => {
        const { data } = await api.get(`/gate-check/deliveries?scope=${scope}`);
        setItems(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);
    useEffect(() => {
        api.get("/gate-check/gates").then((r) => setGates(r.data)).catch(() => {});
        api.get("/gate-check/deliveries/departments").then((r) => setDepartments(r.data)).catch(() => {});
    }, []);

    const visibleTabs = useMemo(() => TABS.filter((t) => t.key !== "all" || superset), [superset]);

    return (
        <div>
            <PageHeader
                overline="CheckPoint · Deliveries & Couriers"
                title="Inbound Deliveries"
                subtitle="3-stage flow: someone pre-logs, the gate marks arrival, and any teammate in the target department completes receipt."
                actions={has("log_delivery_expected") && (
                    <Button
                        onClick={() => setLogOpen(true)}
                        className="bg-brand hover:bg-brand-dark text-white"
                        data-testid="log-delivery-btn"
                    >
                        <Plus size={14} className="mr-1" /> Log Expected Delivery
                    </Button>
                )}
            />

            <div className="p-6 space-y-4">
                {/* Tabs */}
                <div className="flex flex-wrap items-center gap-1 border-b border-slate-200">
                    {visibleTabs.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setScope(t.key)}
                            className={`px-3 py-2 text-[12.5px] font-semibold border-b-2 -mb-px transition-colors ${
                                scope === t.key ? "text-brand border-brand" : "text-slate-500 border-transparent hover:text-slate-700"
                            }`}
                            title={t.hint}
                            data-testid={`tab-${t.key}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* List */}
                {items.length === 0 ? (
                    <div className="text-center py-16 text-slate-400">
                        <Package size={48} className="mx-auto mb-3 opacity-40" />
                        <div className="text-sm">No deliveries in this view</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {items.map((d) => {
                            const stage = STATUS_META[d.status]?.dot || 1;
                            const canMarkArrived = has("mark_delivery_arrived") && d.status === "expected";
                            const canReceive = d.status === "arrived_at_gate" && (
                                superset ||
                                (has("receive_delivery") && (user?.department || "").toLowerCase().trim() === (d.intended_for_department || "").toLowerCase().trim())
                            );
                            return (
                                <Card key={d.id} className="p-4 space-y-2 hover:shadow-sm transition-shadow" data-testid={`delivery-card-${d.id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <button
                                            onClick={() => setDetail(d)}
                                            className="text-left min-w-0 flex-1 group"
                                            data-testid={`delivery-open-${d.id}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Truck size={14} className="text-brand shrink-0" />
                                                <div className="font-semibold text-slate-800 group-hover:text-brand truncate">
                                                    {d.courier_company}
                                                    {d.tracking_number && <span className="text-slate-400 font-mono text-[11px] ml-1">· {d.tracking_number}</span>}
                                                </div>
                                            </div>
                                            <div className="text-[11.5px] text-slate-500 mt-0.5 truncate">
                                                <User size={11} className="inline mr-0.5" /> For {d.intended_for_name}
                                                <span className="mx-1">·</span>
                                                <Buildings size={11} className="inline mr-0.5" /> {d.intended_for_department}
                                            </div>
                                        </button>
                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                            <StatusPill status={d.status} />
                                            <StageDots stage={stage} />
                                        </div>
                                    </div>
                                    <div className="text-[11px] text-slate-500 pt-1 border-t border-slate-100">
                                        <div>Logged by <b>{d.logged_by_name}</b> · {fmtIST(d.logged_at || d.created_at, { date: true, time: false })}</div>
                                        {d.arrived_at_gate_at && (
                                            <div className="text-sky-700">At gate: {d.gate_name} · {d.arrived_by_guard_name}</div>
                                        )}
                                        {d.received_at && (
                                            <div className="text-emerald-700">Received by <b>{d.received_by_name}</b> · {fmtIST(d.received_at)}</div>
                                        )}
                                    </div>
                                    <div className="flex gap-2 pt-1">
                                        {canMarkArrived && (
                                            <Button
                                                size="sm" variant="outline" className="flex-1 h-8 text-[11.5px]"
                                                onClick={() => setArrivedFor(d)}
                                                data-testid={`mark-arrived-${d.id}`}
                                            >
                                                <MapPin size={12} className="mr-1" /> Mark at gate
                                            </Button>
                                        )}
                                        {canReceive && (
                                            <Button
                                                size="sm" className="flex-1 h-8 text-[11.5px] bg-emerald-600 hover:bg-emerald-700 text-white"
                                                onClick={() => setReceiveFor(d)}
                                                data-testid={`mark-received-${d.id}`}
                                            >
                                                <CheckCircle size={12} className="mr-1" /> Mark received
                                            </Button>
                                        )}
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>

            <LogDeliveryDialog
                open={logOpen}
                onClose={() => setLogOpen(false)}
                onSuccess={load}
                departments={departments}
                gates={gates}
                superset={superset}
            />
            <MarkArrivedDialog
                delivery={arrivedFor}
                onClose={() => setArrivedFor(null)}
                onSuccess={load}
                gates={gates}
            />
            <MarkReceivedDialog
                delivery={receiveFor}
                onClose={() => setReceiveFor(null)}
                onSuccess={load}
            />
            <DeliveryDetailDialog
                delivery={detail}
                onClose={() => setDetail(null)}
            />
        </div>
    );
}

// ─────────── Stage 1 dialog: log expected delivery (or walk-in) ───────────
function LogDeliveryDialog({ open, onClose, onSuccess, departments, gates, superset }) {
    const { user } = useAuth();
    const canWalkIn = ["admin", "admin_addl", "guard"].includes(user?.role);
    const canManageTypes = ["admin", "admin_addl"].includes(user?.role);
    const [directory, setDirectory] = useState([]);
    const [dirOpen, setDirOpen] = useState(false);
    const [packageTypes, setPackageTypes] = useState([]);
    const [manageTypesOpen, setManageTypesOpen] = useState(false);
    const [form, setForm] = useState({
        courier_company: "", tracking_number: "",
        intended_for_user_id: "", intended_for_name: "", intended_for_department: "",
        deptOverridden: false,   // once the picker sets a dept, we mark it locked
        package_type: "", notes: "",
        walk_in: false, gate_id: "", arrival_photo_base64: "",
    });
    const [busy, setBusy] = useState(false);
    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

    // Fetch the lightweight directory once the dialog opens.
    useEffect(() => {
        if (!open || directory.length) return;
        api.get("/gate-check/deliveries/directory").then((r) => setDirectory(r.data)).catch(() => {});
    }, [open, directory.length]);

    const loadPackageTypes = async () => {
        try {
            const { data } = await api.get("/gate-check/lookups/package-types");
            const vals = data?.values || [];
            setPackageTypes(vals);
            // Default the field to the first value if it isn't set (or was set to
            // something the admin has since removed).
            setForm((f) => {
                if (f.package_type && vals.includes(f.package_type)) return f;
                return { ...f, package_type: vals[0] || "" };
            });
        } catch { /* silent */ }
    };
    useEffect(() => { if (open) loadPackageTypes(); /* eslint-disable-next-line */ }, [open]);

    const selectedUser = directory.find((u) => u.id === form.intended_for_user_id);

    // If a department is picked (either manually or auto-filled), narrow the
    // recipient dropdown to just that department's users. When no dept is set,
    // show everyone.
    const filteredDirectory = (() => {
        const dept = (form.intended_for_department || "").trim().toLowerCase();
        if (!dept) return directory;
        return directory.filter(
            (u) => (u.department || "").trim().toLowerCase() === dept,
        );
    })();

    const pickRecipient = (u) => {
        setForm((f) => ({
            ...f,
            intended_for_user_id: u.id,
            intended_for_name: u.name,
            intended_for_department: u.department,   // ← auto-fill from user
            deptOverridden: false,
        }));
        setDirOpen(false);
    };

    // Dept picker (available to superset users). If the newly-picked dept
    // doesn't match the currently-selected recipient's dept, clear the
    // recipient — otherwise we'd have a mismatched pair.
    const pickDepartment = (dept) => {
        setForm((f) => {
            const nextDept = (dept || "").trim();
            const currentUser = directory.find((u) => u.id === f.intended_for_user_id);
            const mismatch =
                currentUser &&
                (currentUser.department || "").trim().toLowerCase() !== nextDept.toLowerCase();
            return {
                ...f,
                intended_for_department: nextDept,
                deptOverridden: true,
                ...(mismatch
                    ? { intended_for_user_id: "", intended_for_name: "" }
                    : {}),
            };
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.courier_company || !form.intended_for_name || !form.intended_for_department) {
            toast.error("Courier, recipient, and department are required");
            return;
        }
        if (form.walk_in && !form.gate_id) {
            toast.error("Pick a gate for walk-in couriers");
            return;
        }
        setBusy(true);
        try {
            const { deptOverridden: _drop, ...payload } = form;
            await api.post("/gate-check/deliveries", payload);
            toast.success(form.walk_in ? "Walk-in courier logged" : "Delivery logged — waiting for arrival");
            setForm({ courier_company: "", tracking_number: "", intended_for_user_id: "", intended_for_name: "", intended_for_department: "", deptOverridden: false, package_type: packageTypes[0] || "", notes: "", walk_in: false, gate_id: "", arrival_photo_base64: "" });
            onClose?.(); onSuccess?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to log delivery");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-lg" data-testid="log-delivery-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Package className="text-brand" size={18} /> Log expected inbound delivery
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Courier company *">
                            <Input value={form.courier_company} onChange={set("courier_company")} placeholder="DHL, BlueDart, DTDC…" data-testid="log-courier" className="h-10" />
                        </Field>
                        <Field label="Tracking number">
                            <Input value={form.tracking_number} onChange={set("tracking_number")} placeholder="Optional" data-testid="log-tracking" className="h-10" />
                        </Field>

                        {/* Recipient combobox — picks from Ops IQ users and auto-fills dept. */}
                        <Field label="Intended recipient *">
                            <Popover open={dirOpen} onOpenChange={setDirOpen}>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white flex items-center justify-between text-[13px] hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand"
                                        data-testid="log-recipient-btn"
                                    >
                                        <span className={selectedUser ? "text-slate-800 truncate" : "text-slate-400"}>
                                            {selectedUser
                                                ? selectedUser.name
                                                : form.intended_for_department
                                                    ? `Pick from ${form.intended_for_department}…`
                                                    : "Pick a teammate…"}
                                        </span>
                                        <CaretUpDown size={14} className="text-slate-400 shrink-0" />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-[--radix-popover-trigger-width] p-0"
                                    align="start"
                                    // Radix Dialog otherwise swallows wheel/touch events before they
                                    // hit the cmdk list. Stopping propagation lets the browser scroll
                                    // the popover natively (mousepad / two-finger / touch flick).
                                    onWheel={(e) => e.stopPropagation()}
                                    onTouchMove={(e) => e.stopPropagation()}
                                    onOpenAutoFocus={(e) => e.preventDefault()}
                                >
                                    <Command>
                                        <CommandInput placeholder="Search by name…" data-testid="log-recipient-search" />
                                        <CommandList
                                            className="max-h-[280px] overflow-y-auto overscroll-contain touch-pan-y"
                                            onWheel={(e) => e.stopPropagation()}
                                            onTouchMove={(e) => e.stopPropagation()}
                                        >
                                            <CommandEmpty>{form.intended_for_department ? `No users in "${form.intended_for_department}"` : "No matching user"}</CommandEmpty>
                                            <CommandGroup>
                                                {filteredDirectory.map((u) => (
                                                    <CommandItem
                                                        key={u.id}
                                                        value={u.name}
                                                        onSelect={() => pickRecipient(u)}
                                                        data-testid={`log-recipient-opt-${u.id}`}
                                                    >
                                                        <div className="flex-1 min-w-0 text-[13px] font-medium text-slate-800 truncate">{u.name}</div>
                                                        {form.intended_for_user_id === u.id && <Check size={14} className="text-brand shrink-0 ml-2" />}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </Field>

                        {/* Department — auto-filled from picked user, but admin/guard can override. */}
                        <Field label="Recipient department *">
                            <Select
                                value={form.intended_for_department}
                                onValueChange={pickDepartment}
                                disabled={!superset && !!selectedUser}
                            >
                                <SelectTrigger className="h-10" data-testid="log-department">
                                    <SelectValue placeholder="Auto-fills from recipient" />
                                </SelectTrigger>
                                <SelectContent>
                                    {departments.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
                                </SelectContent>
                            </Select>
                            {selectedUser && !superset && (
                                <div className="text-[10.5px] text-slate-400 mt-1">Locked to {selectedUser.name}'s department</div>
                            )}
                        </Field>

                        <Field
                            label={
                                <span className="flex items-center gap-2">
                                    <span>Package type</span>
                                    {canManageTypes && (
                                        <button
                                            type="button"
                                            onClick={() => setManageTypesOpen(true)}
                                            className="text-[10.5px] font-mono tracking-wider uppercase text-brand hover:text-brand-dark hover:underline"
                                            data-testid="log-package-type-manage"
                                        >
                                            Manage
                                        </button>
                                    )}
                                </span>
                            }
                        >
                            <Select value={form.package_type} onValueChange={set("package_type")}>
                                <SelectTrigger className="h-10" data-testid="log-package-type"><SelectValue placeholder="Pick a type" /></SelectTrigger>
                                <SelectContent>
                                    {packageTypes.map((v) => (
                                        <SelectItem key={v} value={v}>{v}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Notes">
                            <Input value={form.notes} onChange={set("notes")} placeholder="Anything the guard should know" data-testid="log-notes" className="h-10" />
                        </Field>
                    </div>

                    {canWalkIn && (
                        <div className="rounded-md border border-sky-200 bg-sky-50 p-3">
                            <label className="flex items-center gap-2 text-[12.5px] text-sky-900 font-semibold cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.walk_in}
                                    onChange={(e) => setForm((f) => ({ ...f, walk_in: e.target.checked }))}
                                    data-testid="log-walkin"
                                />
                                Walk-in courier — parcel is at my gate right now (combine Stage 1 + 2)
                            </label>
                            {form.walk_in && (
                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <Field label="Gate *">
                                        <Select value={form.gate_id} onValueChange={set("gate_id")}>
                                            <SelectTrigger className="h-10" data-testid="log-gate"><SelectValue placeholder="Pick gate" /></SelectTrigger>
                                            <SelectContent>
                                                {gates.map((g) => (<SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={busy} className="bg-brand hover:bg-brand-dark text-white" data-testid="log-submit">
                            {busy ? "Logging…" : "Log delivery"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
            <ManagePackageTypesDialog
                open={manageTypesOpen}
                onClose={() => setManageTypesOpen(false)}
                values={packageTypes}
                onSaved={(next) => {
                    setPackageTypes(next);
                    setForm((f) => (next.includes(f.package_type) ? f : { ...f, package_type: next[0] || "" }));
                }}
            />
        </Dialog>
    );
}

// ─────────── Admin-only: manage the Package Type catalog ───────────
function ManagePackageTypesDialog({ open, onClose, values, onSaved }) {
    const [items, setItems] = useState(values || []);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (open) { setItems(values || []); setInput(""); } }, [open, values]);

    const add = () => {
        const s = input.trim();
        if (!s) return;
        if (items.some((v) => v.toLowerCase() === s.toLowerCase())) {
            toast.info("Already in the list"); return;
        }
        setItems((xs) => [...xs, s]);
        setInput("");
    };
    const remove = (v) => setItems((xs) => xs.filter((x) => x !== v));
    const move = (idx, dir) => {
        setItems((xs) => {
            const next = [...xs];
            const j = idx + dir;
            if (j < 0 || j >= next.length) return xs;
            [next[idx], next[j]] = [next[j], next[idx]];
            return next;
        });
    };
    const save = async () => {
        if (!items.length) { toast.error("Keep at least one package type"); return; }
        setBusy(true);
        try {
            const { data } = await api.put("/gate-check/lookups/package-types", { values: items });
            toast.success("Package types updated");
            onSaved?.(data.values || items);
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="manage-package-types-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Package className="text-brand" size={18} /> Manage Package Types
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="text-[12px] text-slate-500">
                        Admin-managed list powering the Package Type dropdown on the Log Expected Delivery form. Order shown here is the order users see.
                    </div>
                    <div className="rounded-md border border-slate-200 divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
                        {items.length === 0 && (
                            <div className="p-3 text-[12px] text-slate-400 italic">No package types yet — add one below.</div>
                        )}
                        {items.map((v, i) => (
                            <div key={v} className="flex items-center gap-2 px-3 py-2" data-testid={`pt-row-${v}`}>
                                <div className="flex-1 text-[13px] font-medium text-slate-800 truncate">{v}</div>
                                <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                                        className="text-[11px] font-mono text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up">▲</button>
                                <button type="button" onClick={() => move(i, +1)} disabled={i === items.length - 1}
                                        className="text-[11px] font-mono text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down">▼</button>
                                <button type="button" onClick={() => remove(v)}
                                        className="text-[11px] font-semibold text-rose-500 hover:text-rose-700"
                                        data-testid={`pt-remove-${v}`}>Remove</button>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                            placeholder="Add a new type (e.g. Machinery)"
                            className="h-10"
                            data-testid="pt-input"
                        />
                        <Button type="button" onClick={add} disabled={!input.trim()} className="h-10 bg-brand hover:bg-brand-dark text-white" data-testid="pt-add">
                            <Plus size={14} className="mr-1" /> Add
                        </Button>
                    </div>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                    <Button type="button" onClick={save} disabled={busy} className="bg-brand hover:bg-brand-dark text-white" data-testid="pt-save">
                        {busy ? "Saving…" : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─────────── Stage 2 dialog: guard marks arrived at gate ───────────
function MarkArrivedDialog({ delivery, onClose, onSuccess, gates }) {
    const [gateId, setGateId] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (delivery && gates?.length) setGateId(gates[0].id);
    }, [delivery, gates]);

    const submit = async () => {
        if (!gateId) { toast.error("Pick a gate"); return; }
        setBusy(true);
        try {
            await api.post(`/gate-check/deliveries/${delivery.id}/mark-arrived`, { gate_id: gateId, notes });
            toast.success(`${delivery.courier_company} marked at gate`);
            onClose?.(); onSuccess?.();
            setNotes("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Mark arrived failed");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={!!delivery} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="arrived-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MapPin className="text-sky-600" size={18} /> Mark arrived at gate
                    </DialogTitle>
                </DialogHeader>
                {delivery && (
                    <div className="space-y-3">
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12.5px]">
                            <div className="font-semibold text-slate-800">{delivery.courier_company} · {delivery.tracking_number || "no tracking"}</div>
                            <div className="text-slate-600">For {delivery.intended_for_name} · {delivery.intended_for_department}</div>
                            <div className="text-slate-500 text-[11.5px]">Logged by {delivery.logged_by_name}</div>
                        </div>
                        <Field label="Gate *">
                            <Select value={gateId} onValueChange={setGateId}>
                                <SelectTrigger className="h-10" data-testid="arrived-gate"><SelectValue placeholder="Gate" /></SelectTrigger>
                                <SelectContent>
                                    {gates?.map((g) => (<SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Notes (optional)">
                            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any observation about the parcel" data-testid="arrived-notes" />
                        </Field>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="arrived-submit">
                        {busy ? "Marking…" : "Confirm at gate"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─────────── Stage 3 dialog: department member confirms receipt ───────────
function MarkReceivedDialog({ delivery, onClose, onSuccess }) {
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/gate-check/deliveries/${delivery.id}/mark-received`, { notes });
            toast.success("Marked received — thanks!");
            onClose?.(); onSuccess?.();
            setNotes("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Mark received failed");
        } finally { setBusy(false); }
    };
    return (
        <Dialog open={!!delivery} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="received-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CheckCircle className="text-emerald-600" size={18} /> Confirm final receipt
                    </DialogTitle>
                </DialogHeader>
                {delivery && (
                    <div className="space-y-3">
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12.5px]">
                            <div className="font-semibold text-slate-800">{delivery.courier_company} · {delivery.tracking_number || "no tracking"}</div>
                            <div className="text-slate-600">For {delivery.intended_for_name} · {delivery.intended_for_department}</div>
                            <div className="text-slate-500 text-[11.5px]">At gate since {fmtIST(delivery.arrived_at_gate_at || delivery.created_at)}</div>
                        </div>
                        <Field label="Notes (optional)">
                            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Where you kept it, any damage, etc." data-testid="received-notes" />
                        </Field>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="received-submit">
                        {busy ? "Confirming…" : "Confirm received"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─────────── Read-only timeline drawer ───────────
function DeliveryDetailDialog({ delivery, onClose }) {
    if (!delivery) return null;
    const steps = [
        { key: "logged",   title: "Logged",       done: true, when: delivery.logged_at || delivery.created_at, who: delivery.logged_by_name, dept: delivery.logged_by_department, notes: delivery.notes },
        { key: "arrived",  title: "At gate",      done: !!delivery.arrived_at_gate_at, when: delivery.arrived_at_gate_at, who: delivery.arrived_by_guard_name, dept: delivery.gate_name, notes: delivery.arrival_notes },
        { key: "received", title: "Received",     done: !!delivery.received_at, when: delivery.received_at, who: delivery.received_by_name, dept: delivery.received_by_department, notes: delivery.receipt_notes },
    ];
    return (
        <Dialog open={!!delivery} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="delivery-detail">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Package className="text-brand" size={18} /> {delivery.courier_company}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-[13px] space-y-1">
                        <div><span className="text-slate-500">Tracking:</span> <span className="font-mono">{delivery.tracking_number || "—"}</span></div>
                        <div><span className="text-slate-500">Package:</span> {delivery.package_type}</div>
                        <div><span className="text-slate-500">For:</span> {delivery.intended_for_name} <span className="text-slate-400">·</span> {delivery.intended_for_department}</div>
                    </div>
                    <div className="border-t border-slate-100 pt-3 space-y-3">
                        {steps.map((s, i) => (
                            <div key={s.key} className="flex gap-3">
                                <div className="flex flex-col items-center">
                                    <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold ${s.done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                                        {i + 1}
                                    </div>
                                    {i < steps.length - 1 && <div className={`w-px flex-1 ${s.done ? "bg-emerald-300" : "bg-slate-200"}`} />}
                                </div>
                                <div className="flex-1 pb-3">
                                    <div className="font-semibold text-slate-800 text-[13px]">{s.title}</div>
                                    {s.done ? (
                                        <div className="text-[12px] text-slate-600">
                                            <div>{s.who} <span className="text-slate-400">· {s.dept || "—"}</span></div>
                                            <div className="text-slate-500">{s.when ? fmtIST(s.when) : "—"}</div>
                                            {s.notes && <div className="text-[11.5px] text-slate-500 italic mt-0.5">"{s.notes}"</div>}
                                        </div>
                                    ) : (
                                        <div className="text-[12px] text-slate-400">Pending</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-[11px] uppercase tracking-widest text-slate-500">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
